import type { EditTimeReportData } from "services/api/company/timeReports.service";
import { err, ok } from "utils/result";
import type { Result } from "utils/result";
import {
  mergeIsoDateWithTimeInput,
  normalizeTimeReportTimesOvernight,
} from "web-apps/company/utils/timeAndDate";

interface ResolveEditedTimeReportTimesParams {
  readonly baseStartTime: string | null;
  readonly editedBreakEnd: string;
  readonly editedBreakStart: string;
  readonly editedEnd: string;
  readonly editedStart: string;
}

export function resolveEditedTimeReportTimes({
  baseStartTime,
  editedBreakEnd,
  editedBreakStart,
  editedEnd,
  editedStart,
}: ResolveEditedTimeReportTimesParams): Result<
  EditTimeReportData,
  "invalid-edited-times"
> {
  const editedTimes = mergeEditedTimesOnReportStartDate({
    baseStartTime,
    editedBreakEnd,
    editedBreakStart,
    editedEnd,
    editedStart,
  });
  if (!editedTimes.ok) return editedTimes;
  if (editedTimes.value.endTime === editedTimes.value.startTime) {
    return err("invalid-edited-times");
  }

  const normalizedTimes = normalizeTimeReportTimesOvernight(editedTimes.value);
  return normalizedTimes == null
    ? err("invalid-edited-times")
    : ok(normalizedTimes);
}

// The editor only supplies clock values, so every edited boundary first shares
// the report start date before an earlier clock value rolls into the next day.
function mergeEditedTimesOnReportStartDate({
  baseStartTime,
  editedBreakEnd,
  editedBreakStart,
  editedEnd,
  editedStart,
}: ResolveEditedTimeReportTimesParams): Result<
  EditTimeReportData,
  "invalid-edited-times"
> {
  const startTime = mergeIsoDateWithTimeInput(baseStartTime, editedStart);
  const endTime = mergeIsoDateWithTimeInput(baseStartTime, editedEnd);
  const breakStartTime = mergeIsoDateWithTimeInput(
    baseStartTime,
    editedBreakStart,
  );
  const breakEndTime = mergeIsoDateWithTimeInput(baseStartTime, editedBreakEnd);

  if (
    startTime == null ||
    endTime == null ||
    breakStartTime == null ||
    breakEndTime == null
  ) {
    return err("invalid-edited-times");
  }

  return ok({ startTime, endTime, breakStartTime, breakEndTime });
}
